import { ClassNames, css } from '@emotion/react';
import { useHover } from '@react-aria/interactions';

import { FoldCarret } from '~src/icons/fold-carret';
import { colors } from '~src/theme/colors';
import { fontStyles } from '~src/theme/fontStyles';
import { spacing } from '~src/theme/spacing';

export interface FoldButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  folded: boolean;
  foldedText?: string;
  textStyle?: React.CSSProperties;
  unfoldedText?: string;
  ariaSupplementaryText?: string;
}

export const FoldButton: React.FC<FoldButtonProps> = ({
  folded,
  foldedText,
  textStyle,
  unfoldedText,
  ariaSupplementaryText = '',
  className,
  ...otherProps
}) => {
  const { hoverProps, isHovered } = useHover({});

  return (
    <ClassNames>
      {({ cx }) => (
        <button
          aria-expanded={!folded}
          aria-label={folded ? `${foldedText} (${ariaSupplementaryText})`.trimEnd() : unfoldedText}
          {...hoverProps}
          className={cx(className, 'focus-visible-outline')}
          css={styles.foldButton(!!isHovered)}
          {...otherProps}
        >
          <span css={styles.buttonText} style={textStyle}>
            {folded ? foldedText : unfoldedText}
          </span>
          <div aria-hidden="true" css={styles.foldCarretIcon(!folded)}>
            <FoldCarret ariaName={foldedText} color={colors.black} />
          </div>
        </button>
      )}
    </ClassNames>
  );
};

const styles = {
  foldButton: (isHovered: boolean) =>
    css({
      textDecoration: isHovered ? 'underline' : 'none',
      textUnderlineOffset: '2px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'rgba(0,0,0,0)',
      color: 'black',
      border: 'none',
      width: '100%',
      cursor: 'pointer',
    }),
  buttonText: css({
    ...fontStyles.px14_20,
    letterSpacing: 0,
    marginRight: '8px',
  }),
  foldCarretIcon: (invert: boolean) =>
    css({
      height: spacing.S4,
      lineHeight: spacing.S4,
      transform: invert ? 'rotate(180deg)' : 'none',
      display: 'flex',
      alignItems: 'center',
    }),
};
